import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAX_PRESENCE_APPLICATIONS } from "shared/utils/live-view/protocol";
import { liveViewApi, livePresenceTicketKey } from "api/live-view";
import { useTicketQuery } from "./useLiveViewTicket";
import { openLiveViewSocket, subscribePresence } from "./liveViewSocket";
import {
  EMPTY_PRESENCE,
  type PresenceMap,
  type PresenceRecord
} from "./presence";

export type PresenceByApplication = ReadonlyMap<number, PresenceRecord[]>;

const EMPTY_BY_APPLICATION: PresenceByApplication = new Map();

/** Unique, sorted, at most MAX_PRESENCE_APPLICATIONS: a stable ticket key. */
export const normalizeApplicationIds = (ids: readonly number[]) =>
  [...new Set(ids.filter((id) => Number.isInteger(id) && id > 0))]
    .sort((a, b) => a - b)
    .slice(0, MAX_PRESENCE_APPLICATIONS);

export const groupByApplication = (
  presence: PresenceMap
): PresenceByApplication =>
  [...presence.values()].reduce((byApplication, record) => {
    const current = byApplication.get(record.application_id) ?? [];
    byApplication.set(record.application_id, [...current, record]);
    return byApplication;
  }, new Map<number, PresenceRecord[]>());

/**
 * Who is in the applicant app right now, for a list of applications (the
 * visible rows of the applications table). Uses a presence-only ticket for
 * those ids, re-minted when they change or before it expires; the socket
 * reconnects with each new ticket. Empty when BoomView is off.
 */
export const useLivePresence = (applicationIds: readonly number[]) => {
  const idsKey = normalizeApplicationIds(applicationIds).join(",");
  const ids = useMemo(
    () => (idsKey ? idsKey.split(",").map(Number) : []),
    [idsKey]
  );

  const queryKey = useMemo(() => livePresenceTicketKey(ids), [ids]);
  const queryFn = useCallback(
    () => liveViewApi.createPresenceTicket(ids),
    [ids]
  );
  const { data, getFreshToken } = useTicketQuery({
    queryKey,
    queryFn,
    enabled: ids.length > 0,
    errorToastId: "live-view-presence-ticket"
  });

  // Only the ids the server agreed to; it drops ones the user can't access.
  const allowedKey = data?.enabled ? data.application_ids.join(",") : "";

  const [presence, setPresenceState] = useState<PresenceMap>(EMPTY_PRESENCE);
  const presenceRef = useRef<PresenceMap>(EMPTY_PRESENCE);
  const setPresence = useCallback((next: PresenceMap) => {
    if (next === presenceRef.current) return;
    presenceRef.current = next;
    setPresenceState(next);
  }, []);

  useEffect(() => {
    if (!allowedKey) return;
    const allowed = new Set(allowedKey.split(",").map(Number));

    const { socket, close } = openLiveViewSocket({
      getFreshToken,
      onConnect: () => undefined,
      onDisconnect: ({ dropped }) => {
        if (dropped) setPresence(EMPTY_PRESENCE);
      }
    });
    const stopPruning = subscribePresence(socket, {
      read: () => presenceRef.current,
      write: setPresence,
      accepts: (entry) => allowed.has(entry.application_id)
    });

    return () => {
      stopPruning();
      close();
      setPresence(EMPTY_PRESENCE);
    };
  }, [allowedKey, getFreshToken, setPresence]);

  const byApplication = useMemo(
    () => (presence.size ? groupByApplication(presence) : EMPTY_BY_APPLICATION),
    [presence]
  );

  return { enabled: Boolean(allowedKey), byApplication };
};
